import { useCallback, useEffect } from 'react'
import { getBackboneWindow } from '@opallabs/brown-dwarf'
import {
  EntityContext,
  TextEditorDocument,
  TiptapEditToolCallResult,
} from '@opallabs/gem-copilot'

export enum MessageKeys {
  execute = 'asgard:route:backbone_execute',
  popstate = 'asgard:route:parent_popstate',
  parentNavigation = 'asgard:route:parent_navigation',
  shroudClose = 'asgard:shroud:close',
  shroudOpen = 'asgard:shroud:open',
  fetchAuthentication = 'asgard:authentication:fetch',
  openJumpSearch = 'asgard:jump:open',
  inviteLinkModalTrigger = 'asgard:invite_link_modal:open',
  invalidateQueries = 'asgard:queries:invalidate',
  hideIntercomLauncher = 'asgard:intercom:hide_launcher',
  showIntercomLauncher = 'asgard:intercom:show_launcher',
  showIntercom = 'asgard:intercom:show',
  openCopilot = 'asgard:copilot:open',
  closeCopilot = 'asgard:copilot:close',
  toggleCopilot = 'asgard:copilot:toggle',
  kickoffCopilot = 'asgard:copilot:kickoff',
  setEntityContext = 'asgard:copilot:set_entity_context',
  setTextEditorContext = 'asgard:copilot:set_text_editor_context',
  tiptapEditResult = 'asgard:copilot:tiptap_edit_result',
  contentCreated = 'asgard:copilot:content_created',
  contentUpdated = 'asgard:copilot:content_updated',
  // Tells the calendars in the monolith iframe to refetch their moments after
  // something outside them changes one: Gem, or a monolith modal or shroud.
  // Neither the week view's Redux store nor the month view's own query cache
  // can see those changes. The name is older than its other senders.
  refreshMoments = 'asgard:copilot:refresh_moments',
}

type RoutingMessage = {
  message: MessageKeys.execute | MessageKeys.parentNavigation
  pathname?: string
  replace?: boolean
  route?: string
  hash?: string
  shortPathname?: string
}
type InvalidateQueryMessage = {
  message: MessageKeys.invalidateQueries
  queryKey: string[]
}
type ShroudMessage = {
  message: MessageKeys.shroudOpen | MessageKeys.shroudClose
  type?: string
}
type IntercomLauncherMessage = {
  message: MessageKeys.hideIntercomLauncher | MessageKeys.showIntercomLauncher
}

type CopilotMessage = {
  message:
    | MessageKeys.openCopilot
    | MessageKeys.closeCopilot
    | MessageKeys.toggleCopilot
}

// Fired by surfaces outside asgard (e.g. the monolith iframe) to open Gem with
// a Gem-authored intro. Asgard callers use useCopilotLayout().openCopilotWithIntro directly.
type KickoffCopilotMessage = {
  message: MessageKeys.kickoffCopilot
  content: string
}

type CopilotTransportMessage = {
  message: MessageKeys.setEntityContext
  entity_context: EntityContext
}

type TextEditorContextMessage = {
  message: MessageKeys.setTextEditorContext
  // Absent from an iframe bundle older than the id.
  bridge_id?: string
  documents: TextEditorDocument[]
}

type TiptapEditResultMessage = {
  message: MessageKeys.tiptapEditResult
  result: TiptapEditToolCallResult['result']
  // A kept edit staged again after its editor lost it. Absent from a parent
  // bundle older than the flag.
  restage?: boolean
}

type ContentCreatedMessage = {
  message: MessageKeys.contentCreated
  content_id: string
  moment_id: string
}

type ContentUpdatedMessage = {
  message: MessageKeys.contentUpdated
  content_id: string
}

// Check for specific message payloads given the message key
// If none are matched, then assume the payload has other properties
// available.
type MessageTypes = {
  [MessageKeys.execute]: RoutingMessage
  [MessageKeys.parentNavigation]: RoutingMessage
  [MessageKeys.invalidateQueries]: InvalidateQueryMessage
  [MessageKeys.shroudOpen]: ShroudMessage
  [MessageKeys.shroudClose]: ShroudMessage
  [MessageKeys.hideIntercomLauncher]: IntercomLauncherMessage
  [MessageKeys.showIntercomLauncher]: IntercomLauncherMessage
  [MessageKeys.closeCopilot]: CopilotMessage
  [MessageKeys.openCopilot]: CopilotMessage
  [MessageKeys.toggleCopilot]: CopilotMessage
  [MessageKeys.kickoffCopilot]: KickoffCopilotMessage
  [MessageKeys.setEntityContext]: CopilotTransportMessage
  [MessageKeys.setTextEditorContext]: TextEditorContextMessage
  [MessageKeys.tiptapEditResult]: TiptapEditResultMessage
  [MessageKeys.contentCreated]: ContentCreatedMessage
  [MessageKeys.contentUpdated]: ContentUpdatedMessage
}

type MessageData<T extends MessageKeys> = T extends keyof MessageTypes
  ? MessageTypes[T]
  : { message: T }

type MessageHandler<T extends MessageKeys> = (
  event: MessageEvent<MessageData<T>>
) => void

export const sendMessage = <T extends MessageKeys>(
  key: MessageKeys,
  data: MessageData<T>
): void => {
  const backboneWindow = getBackboneWindow()
  backboneWindow?.Notifier?.trigger(key, data)
}

export const useMessage = <T extends MessageKeys>(
  key: T,
  handler: MessageHandler<T>
): void => {
  const handleMessage = useCallback(
    (event) => {
      if (event.data.message === key) {
        handler(event)
      }
    },
    [key, handler]
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return (): void => window.removeEventListener('message', handleMessage)
  }, [handleMessage])
}
